type PatternEntry = { pattern: RegExp; weight: number };

// ---------------------------------------------------------------------------
// 1.  isLikelyCode  — scoring-based paste detection
// ---------------------------------------------------------------------------

const STRONG_CODE_PATTERNS: PatternEntry[] = [
  { pattern: /\bfunction\s+\w+\s*\(/, weight: 3 },
  { pattern: /\bclass\s+\w+\s*[{(]/, weight: 3 },
  { pattern: /import\s+[\w{}\s*,]+\s+from\s+['"]/, weight: 3 },
  { pattern: /export\s+(default\s+)?(const|let|var|function|class|interface|type)\s/, weight: 3 },
  { pattern: /\bconst\s+\w+\s*=\s*\(.*?\)\s*=>/, weight: 3 },
  { pattern: /\bdef\s+\w+\s*\(/, weight: 3 },
  { pattern: /#include\s*[<"]/, weight: 3 },
  { pattern: /\bfunc\s+\w+\s*\(/, weight: 3 },
  { pattern: /\bfn\s+\w+\s*\(/, weight: 3 },
  { pattern: /\bpub\s+(fn|struct|enum|mod)\b/, weight: 3 },
  { pattern: /public\s+(static\s+)?class\s+\w+/, weight: 3 },
  { pattern: /SELECT\s+.+\s+FROM\s+/i, weight: 3 },
  { pattern: /CREATE\s+(TABLE|INDEX|VIEW)\s+/i, weight: 3 },
  { pattern: /^#!\/.+\b(bash|sh|python|node|perl)\b/m, weight: 3 },
  { pattern: /```[\w-]*\n[\s\S]+?```/, weight: 3 },
  { pattern: /\bpackage\s+[\w.]+;/, weight: 3 },
];

const MEDIUM_CODE_PATTERNS: PatternEntry[] = [
  { pattern: /\b(if|for|while|switch)\s*\(.*\)\s*\{/, weight: 2 },
  { pattern: /^\s*(if|elif|for|while)\s+.+:\s*$/m, weight: 2 },
  { pattern: /^\s+return\s+\S/, weight: 2 },
  { pattern: /\b(const|let|var)\s+\w+\s*=/, weight: 2 },
  { pattern: /(=>|->)\s*[{(]/, weight: 2 },
  { pattern: /\b(async|await)\b/, weight: 2 },
  { pattern: /\.(map|filter|reduce|forEach)\s*\(/, weight: 2 },
  { pattern: /\bthrow\s+new\s+\w+/, weight: 2 },
  { pattern: /\bnew\s+\w+\s*\(/, weight: 2 },
  { pattern: /\b(interface|type|enum)\s+\w+\s*[{=]/, weight: 2 },
  { pattern: /\bself\.\w+/, weight: 2 },
  { pattern: /console\.\w+\s*\(/, weight: 2 },
  { pattern: /\bstd::/, weight: 2 },
  { pattern: /[.#][\w-]+\s*\{[^}]*[:;][^}]*\}/s, weight: 2 },
  { pattern: /\bfrom\s+\w+\s+import\b/, weight: 2 },
  { pattern: /\belif\b/, weight: 2 },
  { pattern: /\bguard\s+let\b/, weight: 2 },
  { pattern: /@(Override|Component|Injectable|Deprecated|Test)\b/, weight: 2 },
  { pattern: /\brequire\s*\(\s*['"]/, weight: 2 },
  { pattern: /\|\s*(grep|awk|sed|sort)\b/, weight: 2 },
];

const WEAK_CODE_PATTERNS: PatternEntry[] = [
  { pattern: /[;{}]\s*$/m, weight: 1 },
  { pattern: /\b(null|undefined|None|nil)\b/, weight: 1 },
  { pattern: /\b(int|float|double|string|bool|void)\s+\w+/, weight: 1 },
  { pattern: /^\s*\/\/\s*\S.+$/m, weight: 1 },
  { pattern: /\/\*[\s\S]*?\*\//, weight: 1 },
  { pattern: /\bprint\s*\(/, weight: 1 },
  { pattern: /\b(True|False)\b/, weight: 1 },
  { pattern: /\$\{[^}]+\}/, weight: 1 },
  { pattern: /\$[a-zA-Z_]\w*/, weight: 1 },
];

function computeStructuralScore(text: string): number {
  const lines = text.split('\n');
  const lineCount = lines.length;
  if (lineCount < 3) return 0;

  let score = 0;

  const nonEmptyLines = lines.filter(l => l.trim().length > 0);
  const indentedLines = nonEmptyLines.filter(l => /^\s{2,}|^\t/.test(l));
  const indentRatio = indentedLines.length / nonEmptyLines.length;
  if (indentRatio > 0.5) score += 3;
  else if (indentRatio > 0.3) score += 2;

  const syntaxLines = nonEmptyLines.filter(l => /[;{}]\s*$/.test(l.trim()));
  const syntaxRatio = syntaxLines.length / nonEmptyLines.length;
  if (syntaxRatio > 0.35) score += 3;
  else if (syntaxRatio > 0.2) score += 2;

  // Consistent indentation levels suggest code structure
  const indentLevels = new Set(
    nonEmptyLines
      .map(l => l.match(/^(\s*)/)?.[1].length ?? 0)
      .filter(n => n > 0)
  );
  if (indentLevels.size >= 2 && indentRatio > 0.25) score += 1;

  return score;
}

/** Negative signals that push the score down (prose-like text). */
function computeProsePenalty(text: string): number {
  const lines = text.split('\n');
  const nonEmpty = lines.filter(l => l.trim().length > 0);
  if (nonEmpty.length === 0) return 0;

  let penalty = 0;

  // Long average line length with mostly alphabetical chars → prose
  const avgLen = nonEmpty.reduce((s, l) => s + l.length, 0) / nonEmpty.length;
  const alphaRatio =
    (text.match(/[a-zA-Z]/g)?.length ?? 0) / Math.max(text.length, 1);

  if (avgLen > 80 && alphaRatio > 0.75) penalty += 2;

  // Very few special characters relative to total length → prose
  const specialChars = (text.match(/[;{}()=<>|&!]/g)?.length ?? 0);
  if (specialChars / Math.max(text.length, 1) < 0.01 && text.length > 200)
    penalty += 2;

  // Sentences ending with periods → prose
  const sentenceLines = nonEmpty.filter(l => /[.!?]\s*$/.test(l.trim()));
  if (sentenceLines.length / nonEmpty.length > 0.4) penalty += 2;

  // Markdown formatting → structured prose, not code
  const markdownLines = nonEmpty.filter(
    l => /^#{1,6}\s|^\*{1,2}\S|^-\s|^\d+\.\s|\[.*\]\(https?:/.test(l.trim())
  );
  if (markdownLines.length / nonEmpty.length > 0.15) penalty += 3;

  // High URL density → web content, not code
  const urlCount = (text.match(/https?:\/\//g)?.length ?? 0);
  if (urlCount > 3) penalty += 2;
  if (urlCount > 10) penalty += 2;

  return penalty;
}

export function isLikelyCode(text: string): boolean {
  if (!text || text.trim().length === 0) return false;

  let score = 0;
  let hasStrongOrMediumHit = false;

  for (const { pattern, weight } of STRONG_CODE_PATTERNS) {
    if (pattern.test(text)) {
      score += weight;
      hasStrongOrMediumHit = true;
    }
  }
  for (const { pattern, weight } of MEDIUM_CODE_PATTERNS) {
    if (pattern.test(text)) {
      score += weight;
      hasStrongOrMediumHit = true;
    }
  }
  for (const { pattern, weight } of WEAK_CODE_PATTERNS) {
    if (pattern.test(text)) score += weight;
  }

  // Structural signals (indentation, syntax line endings) are ambiguous —
  // markdown lists and XML/JSON also produce them.  Only count structure
  // when there is at least one real code pattern backing it up.
  if (hasStrongOrMediumHit) {
    score += computeStructuralScore(text);
  }

  score -= computeProsePenalty(text);

  // Without any recognisable code construct, never trigger
  if (!hasStrongOrMediumHit) return false;

  // Shorter text needs more evidence
  if (text.length < 200) return score >= 7;
  if (text.length < 500) return score >= 5;
  return score >= 4;
}

// ---------------------------------------------------------------------------
// 2.  detectLanguage  — scoring-based language classification
// ---------------------------------------------------------------------------

type LangRule = { lang: string; pattern: RegExp; weight: number };

const LANGUAGE_RULES: LangRule[] = [
  // HTML
  { lang: 'html', pattern: /<!DOCTYPE\s+html>/i, weight: 10 },
  { lang: 'html', pattern: /<html[\s>]/i, weight: 6 },
  { lang: 'html', pattern: /<(div|span|section|header|footer|nav|main|article|aside)\b/i, weight: 3 },
  { lang: 'html', pattern: /<(p|h[1-6]|ul|ol|li|a|img|table|form|input|button)\b/i, weight: 2 },

  // CSS
  { lang: 'css', pattern: /@media\s+/, weight: 5 },
  { lang: 'css', pattern: /@keyframes\s+/, weight: 5 },
  { lang: 'css', pattern: /@import\s+url\(/, weight: 5 },
  { lang: 'css', pattern: /[.#][\w-]+\s*\{[^}]*(margin|padding|display|color|background|font|border|width|height)\s*:/s, weight: 5 },
  { lang: 'css', pattern: /:\s*(flex|grid|block|inline-block|none|absolute|relative|fixed)\s*;/, weight: 4 },
  { lang: 'css', pattern: /\{\s*([\w-]+\s*:\s*[^;]+;\s*){2,}\}/s, weight: 3 },

  // TypeScript (checked before JS so TS-specific features win)
  { lang: 'typescript', pattern: /:\s*(string|number|boolean|void|any|never|unknown)(\[])*\b/, weight: 4 },
  { lang: 'typescript', pattern: /\binterface\s+\w+\s*\{/, weight: 5 },
  { lang: 'typescript', pattern: /\btype\s+\w+\s*=/, weight: 5 },
  { lang: 'typescript', pattern: /<\w+>\s*\(/, weight: 2 },
  { lang: 'typescript', pattern: /\bas\s+(string|number|boolean|any|unknown|\w+)\b/, weight: 3 },
  { lang: 'typescript', pattern: /\benum\s+\w+\s*\{/, weight: 3 },

  // JavaScript (also gives some score to TypeScript since TS is a superset)
  { lang: 'javascript', pattern: /\b(const|let|var)\s+\w+\s*=/, weight: 2 },
  { lang: 'javascript', pattern: /import\s+.*from\s+['"]/, weight: 3 },
  { lang: 'javascript', pattern: /export\s+(default\s+)?(const|let|var|function|class)\b/, weight: 3 },
  { lang: 'javascript', pattern: /=>\s*[{(]/, weight: 2 },
  { lang: 'javascript', pattern: /console\.\w+\s*\(/, weight: 2 },
  { lang: 'javascript', pattern: /\brequire\s*\(\s*['"]/, weight: 3 },
  { lang: 'javascript', pattern: /document\.\w+/, weight: 3 },
  { lang: 'javascript', pattern: /window\.\w+/, weight: 2 },

  // JSX / TSX signals (additive — will combine with JS/TS scores)
  { lang: 'jsx', pattern: /<\w+\s+\w+=\{/, weight: 4 },
  { lang: 'jsx', pattern: /return\s*\(\s*</, weight: 4 },
  { lang: 'jsx', pattern: /className\s*=/, weight: 4 },
  { lang: 'jsx', pattern: /useState|useEffect|useCallback|useMemo|useRef/, weight: 3 },

  // Python
  { lang: 'python', pattern: /\bdef\s+\w+\s*\(/, weight: 4 },
  { lang: 'python', pattern: /^\s*class\s+\w+\s*(\(|:)/m, weight: 4 },
  { lang: 'python', pattern: /\bfrom\s+\w+\s+import\b/, weight: 5 },
  { lang: 'python', pattern: /^\s*import\s+\w+\s*$/m, weight: 3 },
  { lang: 'python', pattern: /\bself\.\w+/, weight: 5 },
  { lang: 'python', pattern: /\bprint\s*\(/, weight: 2 },
  { lang: 'python', pattern: /\belif\b/, weight: 5 },
  { lang: 'python', pattern: /\b(True|False|None)\b/, weight: 2 },
  { lang: 'python', pattern: /^\s*if\s+.+:\s*$/m, weight: 3 },
  { lang: 'python', pattern: /^\s*for\s+\w+\s+in\s+/m, weight: 3 },
  { lang: 'python', pattern: /\b__init__\b/, weight: 5 },
  { lang: 'python', pattern: /\b__\w+__\b/, weight: 3 },

  // Java
  { lang: 'java', pattern: /public\s+(static\s+)?class\s+\w+/, weight: 5 },
  { lang: 'java', pattern: /public\s+static\s+void\s+main/, weight: 6 },
  { lang: 'java', pattern: /System\.out\.print/, weight: 5 },
  { lang: 'java', pattern: /@Override\b/, weight: 4 },
  { lang: 'java', pattern: /\bpackage\s+[\w.]+;/, weight: 5 },
  { lang: 'java', pattern: /\bprivate\s+(static\s+)?(final\s+)?\w+\s+\w+/, weight: 3 },
  { lang: 'java', pattern: /\bimport\s+[\w.]+\.\*?;/, weight: 4 },

  // C
  { lang: 'c', pattern: /#include\s*<[\w./]+>/, weight: 5 },
  { lang: 'c', pattern: /\bprintf\s*\(/, weight: 4 },
  { lang: 'c', pattern: /\bint\s+main\s*\(/, weight: 5 },
  { lang: 'c', pattern: /\bmalloc\s*\(/, weight: 4 },
  { lang: 'c', pattern: /\bsizeof\s*\(/, weight: 3 },
  { lang: 'c', pattern: /\bstruct\s+\w+\s*\{/, weight: 3 },

  // C++
  { lang: 'cpp', pattern: /#include\s*<(iostream|vector|string|map|set|algorithm|memory|fstream)>/, weight: 6 },
  { lang: 'cpp', pattern: /\bstd::/, weight: 5 },
  { lang: 'cpp', pattern: /\bnamespace\s+\w+/, weight: 4 },
  { lang: 'cpp', pattern: /\btemplate\s*</, weight: 5 },
  { lang: 'cpp', pattern: /\bcout\s*<</, weight: 5 },
  { lang: 'cpp', pattern: /\bclass\s+\w+\s*:\s*(public|private|protected)/, weight: 5 },

  // Go
  { lang: 'go', pattern: /\bfunc\s+(\(\w+\s+\*?\w+\)\s+)?\w+\s*\(/, weight: 5 },
  { lang: 'go', pattern: /^\s*package\s+\w+\s*$/m, weight: 4 },
  { lang: 'go', pattern: /\bfmt\./, weight: 5 },
  { lang: 'go', pattern: /:=/, weight: 3 },
  { lang: 'go', pattern: /\bgo\s+func\b/, weight: 5 },
  { lang: 'go', pattern: /\bdefer\s+/, weight: 4 },
  { lang: 'go', pattern: /\bchan\s+\w+/, weight: 5 },

  // Rust
  { lang: 'rust', pattern: /\bfn\s+\w+\s*\(/, weight: 4 },
  { lang: 'rust', pattern: /\blet\s+mut\s+/, weight: 5 },
  { lang: 'rust', pattern: /\bimpl\s+\w+/, weight: 5 },
  { lang: 'rust', pattern: /\bpub\s+(fn|struct|enum|mod)\b/, weight: 5 },
  { lang: 'rust', pattern: /\buse\s+\w+::/, weight: 4 },
  { lang: 'rust', pattern: /\bmatch\s+\w+\s*\{/, weight: 3 },
  { lang: 'rust', pattern: /println!\s*\(/, weight: 5 },
  { lang: 'rust', pattern: /->.*\{/, weight: 2 },

  // Shell / Bash
  { lang: 'shell', pattern: /^#!\/(bin|usr\/bin)\/(bash|sh|zsh)/m, weight: 6 },
  { lang: 'shell', pattern: /\b(echo|grep|sed|awk|curl|wget|chmod|chown|sudo)\b/, weight: 2 },
  { lang: 'shell', pattern: /\b(if|then|fi|elif|else)\b.*;\s*$/m, weight: 3 },
  { lang: 'shell', pattern: /\bfor\s+\w+\s+in\b/, weight: 3 },
  { lang: 'shell', pattern: /\|\s*(grep|awk|sed|sort|uniq|wc|head|tail)\b/, weight: 4 },
  { lang: 'shell', pattern: /\$\(\s*\w+/, weight: 2 },
  { lang: 'shell', pattern: /\bdo\b[\s\S]*\bdone\b/, weight: 3 },

  // SQL
  { lang: 'sql', pattern: /\bSELECT\s+.+\s+FROM\s+/i, weight: 5 },
  { lang: 'sql', pattern: /\bCREATE\s+(TABLE|INDEX|VIEW|DATABASE)\s+/i, weight: 5 },
  { lang: 'sql', pattern: /\b(INSERT\s+INTO|UPDATE\s+\w+\s+SET|DELETE\s+FROM)\b/i, weight: 5 },
  { lang: 'sql', pattern: /\b(WHERE|GROUP\s+BY|ORDER\s+BY|HAVING|JOIN)\b/i, weight: 3 },
  { lang: 'sql', pattern: /\bALTER\s+TABLE\b/i, weight: 5 },

  // Ruby
  { lang: 'ruby', pattern: /\bdef\s+\w+.*\bend\s*$/ms, weight: 4 },
  { lang: 'ruby', pattern: /\bputs\s+/, weight: 3 },
  { lang: 'ruby', pattern: /\battr_(accessor|reader|writer)\b/, weight: 5 },
  { lang: 'ruby', pattern: /\bdo\s*\|/, weight: 3 },
  { lang: 'ruby', pattern: /\brequire\s+['"]/, weight: 2 },
  { lang: 'ruby', pattern: /\bclass\s+\w+\s*<\s*\w+/, weight: 4 },

  // Swift
  { lang: 'swift', pattern: /\bguard\s+let\b/, weight: 5 },
  { lang: 'swift', pattern: /\bfunc\s+\w+\s*\(.*\)\s*(->|{)/, weight: 3 },
  { lang: 'swift', pattern: /\bstruct\s+\w+\s*:\s*\w+/, weight: 3 },
  { lang: 'swift', pattern: /\b(var|let)\s+\w+\s*:\s*\w+/, weight: 2 },
  { lang: 'swift', pattern: /@(IBOutlet|IBAction|Published|State|Binding)\b/, weight: 5 },
  { lang: 'swift', pattern: /\bimport\s+(UIKit|SwiftUI|Foundation)\b/, weight: 6 },

  // Kotlin
  { lang: 'kotlin', pattern: /\bfun\s+\w+\s*\(/, weight: 4 },
  { lang: 'kotlin', pattern: /\bval\s+\w+\s*[:=]/, weight: 3 },
  { lang: 'kotlin', pattern: /\bvar\s+\w+\s*:\s*\w+/, weight: 2 },
  { lang: 'kotlin', pattern: /\bprintln\s*\(/, weight: 3 },
  { lang: 'kotlin', pattern: /\bdata\s+class\b/, weight: 5 },
  { lang: 'kotlin', pattern: /\bcompanion\s+object\b/, weight: 5 },

  // PHP
  { lang: 'php', pattern: /<\?php/, weight: 6 },
  { lang: 'php', pattern: /\$\w+\s*=/, weight: 3 },
  { lang: 'php', pattern: /\bfunction\s+\w+\s*\(.*\$/, weight: 4 },
  { lang: 'php', pattern: /->[\w]+\s*\(/, weight: 2 },
  { lang: 'php', pattern: /\becho\s+['"$]/, weight: 3 },
];

const EXTENSION_MAP: Record<string, string> = {
  javascript: '.js',
  typescript: '.ts',
  jsx: '.jsx',
  tsx: '.tsx',
  python: '.py',
  java: '.java',
  c: '.c',
  cpp: '.cpp',
  go: '.go',
  rust: '.rs',
  html: '.html',
  css: '.css',
  sql: '.sql',
  shell: '.sh',
  ruby: '.rb',
  swift: '.swift',
  kotlin: '.kt',
  php: '.php',
};

export interface LanguageDetectionResult {
  language: string;
  extension: string;
}

export function detectLanguage(code: string): LanguageDetectionResult {
  const scores: Record<string, number> = {};

  for (const { lang, pattern, weight } of LANGUAGE_RULES) {
    if (pattern.test(code)) {
      scores[lang] = (scores[lang] || 0) + weight;
    }
  }

  // JSX/TSX resolution: combine JSX signals with JS/TS base scores
  const jsxScore = scores['jsx'] || 0;
  if (jsxScore > 0) {
    const tsScore = scores['typescript'] || 0;
    const jsScore = scores['javascript'] || 0;

    if (tsScore > jsScore) {
      scores['tsx'] = tsScore + jsxScore;
    } else {
      scores['jsx'] = jsScore + jsxScore;
    }
    delete scores['jsx']; // remove standalone jsx if tsx was chosen
    // re-add if jsx was the winner
    if (!scores['tsx'] || (scores['tsx'] < (jsScore + jsxScore))) {
      scores['jsx'] = jsScore + jsxScore;
    }
  }

  // C vs C++ resolution: if both score, merge C into C++ when C++ is also present
  if (scores['c'] && scores['cpp']) {
    scores['cpp'] += scores['c'];
    delete scores['c'];
  }

  let bestLang = 'javascript';
  let bestScore = 0;
  for (const [lang, score] of Object.entries(scores)) {
    if (score > bestScore) {
      bestScore = score;
      bestLang = lang;
    }
  }

  return {
    language: bestLang,
    extension: EXTENSION_MAP[bestLang] || '.txt',
  };
}

// ---------------------------------------------------------------------------
// 3.  suggestFileName  — language + name extraction
// ---------------------------------------------------------------------------

function extractBaseName(code: string, language: string): string {
  // Language-specific name extractors
  const extractors: Record<string, RegExp[]> = {
    java: [/public\s+class\s+(\w+)/, /class\s+(\w+)/],
    python: [/class\s+(\w+)/, /def\s+(\w+)/],
    rust: [/\bmod\s+(\w+)/, /\bstruct\s+(\w+)/, /\bfn\s+(\w+)/],
    go: [/\bpackage\s+(\w+)/],
    swift: [/\bclass\s+(\w+)/, /\bstruct\s+(\w+)/, /\bprotocol\s+(\w+)/],
    kotlin: [/\bclass\s+(\w+)/, /\bobject\s+(\w+)/],
    ruby: [/\bclass\s+(\w+)/, /\bmodule\s+(\w+)/],
    css: [/[.#]([\w-]+)\s*\{/],
  };

  const genericExtractors: RegExp[] = [
    /export\s+(default\s+)?(?:class|function|const)\s+(\w+)/,
    /class\s+(\w+)/,
    /function\s+(\w+)/,
    /const\s+(\w+)\s*=\s*\(.*?\)\s*=>/,
  ];

  const langPatterns = extractors[language];
  if (langPatterns) {
    for (const p of langPatterns) {
      const m = code.match(p);
      if (m) {
        // export regex captures in group 2 when group 1 is "default", others in group 1
        const name = m[2] || m[1];
        if (name && name.length > 1) return name;
      }
    }
  }

  for (const p of genericExtractors) {
    const m = code.match(p);
    if (m) {
      const name = m[2] || m[1];
      if (name && name.length > 1) return name;
    }
  }

  const defaultNames: Record<string, string> = {
    css: 'styles',
    html: 'index',
    sql: 'query',
    shell: 'script',
  };

  return defaultNames[language] || 'code-snippet';
}

export function suggestFileName(code: string): {
  fileName: string;
  language: string;
} {
  const { language, extension } = detectLanguage(code);
  const baseName = extractBaseName(code, language);
  return {
    fileName: baseName + extension,
    language,
  };
}
